import _ from 'lodash';
import { SkillConfigurationRemoteDto } from 'src/generated/signalR/skill-configuration-remote-dto';
import { SkillUserConfigurationRemoteDto } from 'src/generated/signalR/skill-user-configuration-remote-dto';
import { Skill } from './skill';
import { SkillConstants } from './skill-constants';

export class SkillGroup {
   skillId: number;
   displayName: string;
   oldestCallTimeStamp: Date;
   waitCount: number;
   loggedOnUserIds: number[];
   loggedOnToAllUserIds: number[];
   redirectionAddress: string;
   skills: Skill[];
   skillConfiguration: SkillConfigurationRemoteDto;
   skillUserConfiguration: SkillUserConfigurationRemoteDto;
   activeAgentIds: number[];

   // Add Constructor
   constructor(skills: Skill[]) {
      if (_.isNil(_.head(skills)?.skillConfiguration)) {
         return null;
      }
      this.skillId = _(skills)
         .map((skill) => skill.skillId)
         .min();
      this.oldestCallTimeStamp = _(skills)
         .map((skill) => skill.oldestCallTimeStamp)
         .min();
      this.waitCount = _(skills)
         .map((skill) => skill.waitCount)
         .sum();
      this.loggedOnUserIds = _(skills)
         .flatMap((skill) => skill.loggedOnUserIds)
         .uniq()
         .value();
      this.activeAgentIds = _(skills)
         .flatMap((skill) => skill.activeAgentIds)
         .uniq()
         .value();

      if (this.skillId === SkillConstants.OVERALL_SKILL_ID) {
         // "hack" because a Root Node only has it's own skill in it
         this.loggedOnToAllUserIds = _(skills)
            .flatMap((skill) => skill.loggedOnToAllUserIds)
            .uniq()
            .value();
      } else {
         const loggedOnUsers = _(skills)
            .map((skill) => skill.loggedOnUserIds)
            .value();
         this.loggedOnToAllUserIds = loggedOnUsers.shift()?.filter((v) => {
            return loggedOnUsers.every((a) => {
               return a.indexOf(v) !== -1;
            });
         });
      }

      this.redirectionAddress = _(skills)
         .map((skill) => skill.redirectionAddress)
         .max();

      const skillInfo: SkillConfigurationRemoteDto = _.head(skills).skillConfiguration;
      // change color if group contains other colors
      let skillColor: string = skillInfo.skillColor;
      const colorDifferences = _(skills)
         .filter((skill) => {
            if (skill.skillConfiguration.skillColor !== skillColor) {
               return true;
            }
         })
         .value();

      const allCategories = _(skills)
         .flatMap((skill) => skill.skillConfiguration.categories)
         .uniq()
         .sort()
         .value();

      if (colorDifferences.length > 0) {
         skillColor = '#FFFFFF';
      }

      const groupSkillInfo: SkillConfigurationRemoteDto = {
         id: skillInfo.id,
         skillType: skillInfo.skillType,
         skillName: skillInfo.skillName,
         originName: skillInfo.originName,
         skillColor: skillColor,
         agentParkColor: skillInfo.agentParkColor,
         skillParkColor: skillInfo.skillParkColor,
         skillUrl: skillInfo.skillUrl,
         skillUrlConnected: skillInfo.skillUrlConnected,
         skillUrlConnectedOutbound: skillInfo.skillUrlConnectedOutbound,
         skillUrlDisconnected: skillInfo.skillUrlDisconnected,
         skillUrlRefresh: skillInfo.skillUrlRefresh,
         skillUrlRingback: skillInfo.skillUrlRingback,
         skillUrlStartup: skillInfo.skillUrlStartup,
         isShared: skillInfo.isShared,
         directoryAssociations: skillInfo.directoryAssociations,
         emailConnectorId: skillInfo.emailConnectorId,
         defaultTemplateId: skillInfo.defaultTemplateId,
         skillEmail: skillInfo.skillEmail,
         emailTemplates: skillInfo.emailTemplates,
         hasRecordingAnnouncement: skillInfo.hasRecordingAnnouncement,
         categories: allCategories,
         informWaitingItem: skillInfo.informWaitingItem,
      };

      const isActive = _(skills).some((skill) => skill?.skillUserConfiguration?.isActive);
      const isManageable = _(skills).some((skill) => skill?.skillUserConfiguration?.isManageable);
      const isAccessible = _(skills).some((skill) => skill?.skillUserConfiguration?.isAccessible);
      const userSkillValue = _(skills)
         .map((skill) => skill.skillUserConfiguration.userSkillValue)
         .min();

      const groupUserSkillInfo: SkillUserConfigurationRemoteDto = {
         id: skillInfo.id,
         skillType: skillInfo.skillType,
         userSkillValue: userSkillValue,
         isActive: isActive,
         isManageable: isManageable,
         isAccessible: isAccessible,
      };

      this.skillConfiguration = groupSkillInfo;
      this.skillUserConfiguration = groupUserSkillInfo;
      this.skills = skills;
      this.displayName = skillInfo.skillName;
   }
}
